import { ICoordinate } from "coordinate/model"
import {
  ADD_COORDINATE_IMAGE_ID,
  COORDINATE_CENTER_LAYER_ID,
  COORDINATE_IMAGE_ID,
  CURSOR_DEFAULT,
  CURSOR_MOVE,
  IMapImage,
  IMapInfo,
  INTERSECT_GEO_TARGET_BORDER_LAYER_ID,
  INTERSECT_GEO_TARGET_FILL_LAYER_ID,
  INTERSECT_GEO_TARGET_SOURCE_ID,
  MAPBOX_DEFAULT_LAYER_MAX_ZOOM,
  UPDATE_COORDINATE_IMAGE_ID
} from "form/map/model"
import { getDefaultMapInfo, state } from "form/map/state"
import { GEO_TARGETS_FORM_TABS, IForm } from "form/model"
import {
  getDistanceKind,
  getGeoTargetIntersectionFeature,
  getGeoTargetsForm,
  setGeoTargetsForm,
  startUpdatingGeoTargetsForm,
  stopUpdatingGeoTargetsForm
} from "form/tools/geo-targets"
import { Feature, Point } from "geojson"
import { ILocation } from "geometry/model"
import { GeoJSONSourceRaw, Map, MapboxOptions, NavigationControl, ScaleControl } from "mapbox-gl"
import markerAdd from "resources/images/pin-add.png"
import markerEdit from "resources/images/pin-edit.png"
import markerRegular from "resources/images/pin-regular.png"
import { MAP_COLOR } from "ui/model"
import {
  fitFeaturesOnMap,
  getLocationFromMabBoxEvent,
  getMapInfo,
  getMapMouseCursor,
  getMapboxAccessToken,
  getRenderedFeatures,
  isClusterFeature,
  loadMapImages,
  setMapMouseCursor,
  setSourceData
} from "./common"
import {
  createCoordinatesLayers,
  createTempCoordinateLayers,
  getCoordinatesFeatures,
  setTempCoordinateFeature,
  setTempCoordinateLayersVisibility
} from "./geo-targets-coordinate"
import { createShapeLayers, getShapesFeatures, setTempShapeVisibility } from "./geo-targets-shape"
import { getMapboxLocale } from "./locale"

export function createGeoTargetsMap(containerId: string): Map {
  const container = document.getElementById(containerId)
  if (!container) {
    throw Error("Unable to get geo targets container")
  }
  container.style.position = "relative"
  const options: MapboxOptions = {
    container,
    maxZoom: MAPBOX_DEFAULT_LAYER_MAX_ZOOM - 0.00001,
    renderWorldCopies: true,
    preserveDrawingBuffer: true,
    accessToken: getMapboxAccessToken(),
    style: "mapbox://styles/mapbox/light-v10"
  }
  const mapInfo: IMapInfo = state.geoTargets.mapInfo
  options.bearing = mapInfo.bearing
  options.pitch = mapInfo.pitch
  options.locale = getMapboxLocale()
  if (mapInfo.zoom !== 0) {
    options.zoom = mapInfo.zoom
  }
  if (mapInfo.boundaryBox) {
    options.bounds = [
      mapInfo.boundaryBox.sw.x,
      mapInfo.boundaryBox.sw.y,
      mapInfo.boundaryBox.ne.x,
      mapInfo.boundaryBox.ne.y
    ]
  }
  if (mapInfo.center) {
    options.center = [mapInfo.center.x, mapInfo.center.y]
  }
  const map = new Map(options)
  state.geoTargets.map = map
  setMapMouseCursor(map, CURSOR_DEFAULT)
  let mouseCursorBeforeMapMove = CURSOR_DEFAULT
  map.addControl(new NavigationControl())
  state.geoTargets.scaleControl = new ScaleControl({ unit: getDistanceKind() })
  map.addControl(state.geoTargets.scaleControl)
  const handleMapLoadError = (error: any) => {
    console.error("An error occured while loading the geoTarget map", error)
    map.off("error", handleMapLoadError)
    loadMapData()
  }
  const loadMapData = () => {
    map.off("error", handleMapLoadError)
    createShapeLayers(map, false)
    createCoordinatesLayers(map)
    createIntersectGeoTargetLayers(map)
    createTempCoordinateLayers(map)
    state.geoTargets.coordinateLayersReady = true
    refreshGeoTargetsMap()
    fitAllGeoTargetsFeaturesOnMap(map)
  }
  map.on("error", handleMapLoadError)
  const onStyleLoaded = () => {
    if (map.isStyleLoaded()) {
      map.off("error", handleMapLoadError)
      loadMapData()
    } else {
      setTimeout(() => onStyleLoaded(), 200)
    }
  }
  loadMapImages(map, getGeoTargetMapImages(true)).then(() => onStyleLoaded())
  map.on("click", (event: any) => {
    const location = getLocationFromMabBoxEvent(event)
    const features = getRenderedFeatures(map, getClickableGeoTargetsLayerIds(), location)
    const clusterFeature = features.find(f => isClusterFeature(f))
    if (clusterFeature) {
      // click on a cluster nothing to do
      return
    }
    onGeoTargetMapClick(location, features as any)
  })
  map.on("movestart", () => {
    const currentMouseCursor: string = getMapMouseCursor(map)
    // it happens move-end event not to be triggered,
    // so we have to avoid the move cursor to be always displayed in this case
    if (currentMouseCursor !== CURSOR_MOVE) {
      mouseCursorBeforeMapMove = currentMouseCursor
      setMapMouseCursor(map, CURSOR_MOVE)
    }
  })
  map.on("moveend", () => {
    state.geoTargets.mapInfo = getMapInfo(map)
    setMapMouseCursor(map, mouseCursorBeforeMapMove)
  })
  return map
}

export function displayIntersectedGeoTargets(intersectFeature: Feature | null): void {
  if (state.geoTargets.map) {
    setIntersectGeoTargetSourceData(state.geoTargets.map, intersectFeature)
  }
  if (state.inventory.map) {
    setIntersectGeoTargetSourceData(state.inventory.map, intersectFeature)
  }
  state.featuresByType.intersectGeoTarget = intersectFeature ? intersectFeature : null
}

function setIntersectGeoTargetSourceData(map: Map, feature: Feature | null): void {
  setSourceData(map, INTERSECT_GEO_TARGET_SOURCE_ID, feature ? [feature] : [])
}

export function createIntersectGeoTargetLayers(map: Map): void {
  if (map.getSource(INTERSECT_GEO_TARGET_SOURCE_ID)) {
    return
  }
  map.addSource(INTERSECT_GEO_TARGET_SOURCE_ID, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: state.featuresByType.intersectGeoTarget ? [state.featuresByType.intersectGeoTarget] : []
    },
    maxzoom: 24
  } as GeoJSONSourceRaw)
  map.addLayer(
    {
      id: INTERSECT_GEO_TARGET_FILL_LAYER_ID,
      type: "fill",
      source: INTERSECT_GEO_TARGET_SOURCE_ID,
      paint: {
        "fill-color": MAP_COLOR,
        "fill-opacity": 0.3
      }
    },
    map.getLayer(COORDINATE_CENTER_LAYER_ID) ? COORDINATE_CENTER_LAYER_ID : undefined
  )
  map.addLayer(
    {
      id: INTERSECT_GEO_TARGET_BORDER_LAYER_ID,
      type: "line",
      source: INTERSECT_GEO_TARGET_SOURCE_ID,
      paint: {
        "line-color": "#DE8572",
        "line-width": 1
      }
    },
    map.getLayer(COORDINATE_CENTER_LAYER_ID) ? COORDINATE_CENTER_LAYER_ID : undefined
  )
}

export function destroyGeoTargetsMap(): void {
  if (state.geoTargets.map) {
    state.geoTargets.map.remove()
    state.geoTargets.map = null
    state.geoTargets.coordinateLayersReady = false
    state.geoTargets.scaleControl = null
  }
}

export function getAllGeoTargetintersectLayerIds(): string[] {
  return [INTERSECT_GEO_TARGET_FILL_LAYER_ID, INTERSECT_GEO_TARGET_BORDER_LAYER_ID]
}

function onGeoTargetMapClick(location: ILocation, features: Array<Feature<Point>>): void {
  const geoTargetsForm = getGeoTargetsForm()
  if (geoTargetsForm.activeTab !== GEO_TARGETS_FORM_TABS.COORDINATE) {
    return
  }
  if (features && features.length === 1) {
    startUpdatingGeoTargetsForm(features[0].properties as ICoordinate)
    return
  }
  setGeoTargetsForm({
    latitude: String(location.y),
    longitude: String(location.x)
  })
  setTempCoordinateFeature(location)
}

export function resetGeoTargetsMap(): void {
  destroyGeoTargetsMap()
  state.geoTargets.tempCoordinateFeatures = null
  state.geoTargets.mapInfo = getDefaultMapInfo()
}

export const changeGeoTargetIsImperial = (isImperial: boolean) => {
  if (!state.geoTargets.scaleControl) {
    return
  }
  state.geoTargets.scaleControl.setUnit(isImperial ? "imperial" : "metric")
}

export function refreshGeoTargetsMap(activeTab?: GEO_TARGETS_FORM_TABS): void {
  const map = state.geoTargets.map
  if (!map) {
    return
  }
  const geoTargetsForm = getGeoTargetsForm()
  if (!activeTab) {
    activeTab = geoTargetsForm.activeTab
  }
  if (activeTab === GEO_TARGETS_FORM_TABS.COORDINATE) {
    setTempCoordinateLayersVisibility("visible")
    setTempShapeVisibility(false)
  } else {
    if (!geoTargetsForm.isCreation) {
      stopUpdatingGeoTargetsForm()
    }
    setTempCoordinateLayersVisibility("none")
    setTempShapeVisibility(true)
  }
}

export const setGeoTargetsFeaturesFromform = (form: IForm): void => {
  state.featuresByType = {
    ...state.featuresByType,
    coordinates: getCoordinatesFeatures(form.geoTargets.coordinates),
    shapes: getShapesFeatures(form)
  }
  // getGeoTargetIntersectionFeature use the service state so don't do that before
  const intersectFeature = getGeoTargetIntersectionFeature(form)
  state.featuresByType.intersectGeoTarget = typeof intersectFeature !== "boolean" ? intersectFeature : null
}

export function fitAllGeoTargetsFeaturesOnMap(map: Map, noTempFeature?: boolean): void {
  let features: Feature[] = noTempFeature
    ? state.featuresByType.shapes.filter(f => !f.properties.isTemp)
    : state.featuresByType.shapes
  features = [...features, ...state.featuresByType.coordinates]
  if (features.length === 0) {
    return
  }
  fitFeaturesOnMap(map, features)
}

// isForm => geo targets form map, else inventory map
export function getGeoTargetMapImages(isForm?: boolean): IMapImage[] {
  const images = [{ name: COORDINATE_IMAGE_ID, url: markerRegular }]
  if (!isForm) {
    return images
  }
  images.push({ name: ADD_COORDINATE_IMAGE_ID, url: markerAdd })
  images.push({ name: UPDATE_COORDINATE_IMAGE_ID, url: markerEdit })
  return images
}

function getClickableGeoTargetsLayerIds(): string[] {
  if (!state.geoTargets.coordinateLayersReady) {
    return []
  }
  return [COORDINATE_CENTER_LAYER_ID]
}
